import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { TrendingUp, DollarSign, Receipt, FolderKanban, Users, ChevronDown, ChevronUp, Mail, Phone, MessageCircle, Briefcase, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { MonthRate } from '../components/shared';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  ChartCard, CompositionChart, COMPOSITION_LEGEND, DistributionBar,
  IncomeTrendChart, SimpleTable, StatTile, VizLegend,
  fmtMoney, fmtPct, pctOf, signTone, trimTrailingEmpty,
} from '../components/charts/finops-charts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROJECT_STATUS_TABS = [
  { value: 'active', labelKey: 'status.active' },
  { value: 'paused', labelKey: 'status.paused' },
  { value: 'completed', labelKey: 'status.completed' },
  { value: 'all', labelKey: 'common:actions.all' },
];

// Team Member Profile Card
function TeamMemberCard({ member }) {
  const { t } = useTranslation('dashboard');
  const initials = (member.full_name || member.name || '?')
    .split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
      {member.avatar ? (
        <img src={member.avatar} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{initials}</span>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate flex items-center gap-1.5">
          <span className="truncate">{member.full_name || member.name}</span>
          {member.is_owner && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{t('member.lead')}</span>
          )}
          {member.is_me && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{t('member.you')}</span>
          )}
        </p>
        {member.position && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{member.position}</p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
          {member.email && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Mail className="h-3 w-3" /> {member.email}
            </span>
          )}
          {member.phone && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Phone className="h-3 w-3" /> {member.phone}
            </span>
          )}
          {member.has_telegram && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <MessageCircle className="h-3 w-3" /> Telegram
            </span>
          )}
        </div>
      </div>
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize flex-shrink-0 ${
        member.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' :
        'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
      }`}>{t(`common:status.${member.status}`, { defaultValue: member.status })}</span>
    </div>
  );
}

// Employee Dashboard with expandable project cards
function EmployeeProjectsDashboard({ myProjects, embedded = false, salaryData = null }) {
  const { t } = useTranslation('dashboard');
  const [expandedProject, setExpandedProject] = useState(null);
  const [statusTab, setStatusTab] = useState('active');
  const [tabTouched, setTabTouched] = useState(false);

  const statusCounts = useMemo(() => {
    const counts = { all: myProjects.length };
    myProjects.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return counts;
  }, [myProjects]);

  // Open on "Active", but don't greet a user with an empty tab when all of
  // their projects are paused/completed.
  useEffect(() => {
    if (!tabTouched && myProjects.length > 0 && !statusCounts.active) setStatusTab('all');
  }, [myProjects, statusCounts, tabTouched]);

  const visibleProjects = useMemo(
    () => (statusTab === 'all' ? myProjects : myProjects.filter(p => p.status === statusTab)),
    [myProjects, statusTab]
  );

  const toggleProject = (projectId) => {
    setExpandedProject(prev => prev === projectId ? null : projectId);
  };

  const fmtUzs = (amount) => `${Math.round(amount || 0).toLocaleString()} UZS`;
  const fmtUsd = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {!embedded && (
        <>
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('title')}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('employee.subtitle')}
            </p>
          </div>

          {/* Salary + Project stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employee.myProjects')}</CardTitle>
                <FolderKanban className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{myProjects.length}</div>
                <p className="text-xs text-slate-500 mt-1">{t('employee.assignedCount')}</p>
              </CardContent>
            </Card>

            {salaryData && salaryData.earning_type !== 'none' && (
              <>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employee.currentSalary')}</CardTitle>
                    <Wallet className="h-4 w-4 text-emerald-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-semibold text-emerald-600">
                      {fmtUzs(salaryData.total_earnings_uzs)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      ≈ {fmtUsd(salaryData.total_earnings)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employee.salaryType')}</CardTitle>
                    <Briefcase className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {salaryData.earning_type === 'global_fixed' ? t('employee.fixedSalary') : t('employee.projectBased')}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {salaryData.earning_type === 'global_fixed'
                        ? `${salaryData.salary?.toLocaleString()} ${salaryData.salary_currency || 'USD'}`
                        : t('employee.fromProjects', { count: salaryData.projects?.length || 0 })}
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Salary project breakdown for percentage/project-based employees */}
          {salaryData && salaryData.earning_type === 'project' && salaryData.projects?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('employee.salaryBreakdown')}</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">{t('employee.salaryBreakdownHint')}</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('common:fields.project')}</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('employee.type')}</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('common:fields.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaryData.projects.map((p, idx) => (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-200">{p.project_name}</td>
                          <td className="px-4 py-2 text-slate-600 dark:text-slate-400">
                            {p.payment_type === 'percentage' ? t('employee.percentShare', { percent: p.percentage }) : t('employee.fixedAmount', { amount: p.fixed_amount?.toLocaleString(), currency: p.fixed_currency || 'USD' })}
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-emerald-700 dark:text-emerald-400">
                            {fmtUzs(p.earning_uzs)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {salaryData.global_expense_share_uzs > 0 && (
                        <tr className="border-t border-slate-200 dark:border-slate-700">
                          <td colSpan={2} className="px-4 py-2 text-xs text-red-500">{t('employee.globalExpenseShare')}</td>
                          <td className="px-4 py-2 text-right text-xs text-red-500 font-medium">−{fmtUzs(salaryData.global_expense_share_uzs)}</td>
                        </tr>
                      )}
                      <tr className="border-t-2 border-slate-300 dark:border-slate-600">
                        <td colSpan={2} className="px-4 py-2 font-semibold text-slate-800 dark:text-slate-200">{t('common:fields.total')}</td>
                        <td className="px-4 py-2 text-right font-bold text-emerald-700 dark:text-emerald-400 text-base">
                          {fmtUzs(salaryData.total_earnings_uzs)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {myProjects.length > 0 && (
        <div className="tab-strip flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
          {PROJECT_STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setStatusTab(tab.value); setTabTouched(true); setExpandedProject(null); }}
              data-testid={`my-projects-tab-${tab.value}`}
              className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 whitespace-nowrap ${
                statusTab === tab.value
                  ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {t(tab.labelKey)}
              <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs ${
                statusTab === tab.value
                  ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                {statusCounts[tab.value] || 0}
              </span>
            </button>
          ))}
        </div>
      )}

      {myProjects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t('employee.noProjects')}
          </CardContent>
        </Card>
      ) : visibleProjects.length === 0 ? (
        <div className="text-center py-10 text-slate-400 dark:text-slate-500">
          {t('employee.noProjectsInStatus', { status: t(PROJECT_STATUS_TABS.find(tab => tab.value === statusTab)?.labelKey) })}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleProjects.map((proj) => {
            const isExpanded = expandedProject === proj.project_id;
            return (
              <Card key={proj.project_id} className="overflow-hidden transition-shadow hover:shadow-md">
                <div
                  className="cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleProject(proj.project_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleProject(proj.project_id);
                    }
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg">{proj.name}</CardTitle>
                        {proj.description && (
                          <p className="text-sm text-slate-500 mt-0.5 truncate">{proj.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                          proj.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' :
                          proj.status === 'completed' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' :
                          'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>{t(`status.${proj.status}`, { defaultValue: proj.status })}</span>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {!isExpanded && proj.team && proj.team.length > 0 && (
                    <CardContent className="pt-0 pb-3">
                      <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-2">
                          {proj.team.slice(0, 5).map((member, idx) => (
                            member.avatar ? (
                              <img key={idx} src={member.avatar} alt="" className="h-7 w-7 rounded-full border-2 border-white dark:border-slate-800 object-cover" />
                            ) : (
                              <div key={idx} className="h-7 w-7 rounded-full border-2 border-white dark:border-slate-800 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">
                                  {(member.full_name || member.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                        {proj.team.length > 5 && (
                          <span className="text-xs text-slate-500 ml-1">+{proj.team.length - 5}</span>
                        )}
                        <span className="text-xs text-slate-400 ml-2">
                          {t('employee.members', { count: proj.team.length })}
                        </span>
                      </div>
                    </CardContent>
                  )}
                </div>

                {isExpanded && (
                  <CardContent className="pt-0 border-t border-slate-100 dark:border-slate-700">
                    <div className="pt-3">
                      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" /> {t('employee.teamMembers', { count: proj.team?.length || 0 })}
                      </p>
                      {proj.team && proj.team.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {proj.team.map((member, idx) => (
                            <TeamMemberCard key={idx} member={member} />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 py-2">{t('employee.noTeamMembers')}</p>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const { t } = useTranslation('dashboard');
  const months = t('common:months', { returnObjects: true });
  const monthsShort = useMemo(() => t('common:monthsShort', { returnObjects: true }), [t]);
  const { user: currentUser } = useAuth();
  const isEmployee = currentUser?.role === 'employee';
  const canViewFinance = currentUser?.can_view_finance;

  const [summary, setSummary] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [mySalary, setMySalary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [currencyFilter, setCurrencyFilter] = useState('combined');
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  // The selected month's rate (set by <MonthRate>): its USD figures are at that rate
  const [exchangeRate, setExchangeRate] = useState(null);

  // Currency display helpers
  const fmtUsd = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtUzs = (amount) => `${Math.round((amount || 0)).toLocaleString()} UZS`;
  const displayAmount = useCallback((usdAmount, uzsAmount) => {
    if (displayCurrency === 'UZS') {
      // Use pre-calculated UZS amount from backend if available; otherwise fall back to conversion
      if (uzsAmount !== undefined && uzsAmount !== null) return fmtUzs(uzsAmount);
      if (exchangeRate) return fmtUzs(usdAmount * exchangeRate);
    }
    return fmtUsd(usdAmount);
  }, [displayCurrency, exchangeRate]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Employee without finance access: only fetch their projects + salary
      if (isEmployee && !canViewFinance) {
        const now = new Date();
        const [projRes, salaryRes] = await Promise.all([
          axios.get(`${API_URL}/api/my/projects`, { withCredentials: true }),
          axios.get(`${API_URL}/api/my/salary?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setMyProjects(projRes.data || []);
        setMySalary(salaryRes.data || null);
        setLoading(false);
        return;
      }

      const [summaryRes, trendRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/summary?month=${selectedMonth}&year=${selectedYear}&currency_filter=${currencyFilter}`, {
          withCredentials: true
        }),
        axios.get(`${API_URL}/api/reports/monthly-trend?year=${selectedYear}&currency_filter=${currencyFilter}`, {
          withCredentials: true
        })
      ]);

      setSummary(summaryRes.data);
      setMonthlyTrend(trendRes.data);

      // Load assigned projects for all roles
      try {
        const projRes = await axios.get(`${API_URL}/api/my/projects`, { withCredentials: true });
        setMyProjects(projRes.data || []);
      } catch (e) {
        // Non-critical — projects section just won't show
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, currencyFilter, isEmployee, canViewFinance, t]);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedMonth, selectedYear, fetchDashboardData]);

  // Shape the year's trend for the charts. Salary / operational / retained sum
  // back to income, so one stacked series tells the whole story instead of two
  // near-identical lines drawn on top of each other.
  const trendRows = useMemo(() => {
    const uzs = displayCurrency === 'UZS';
    const rows = (monthlyTrend || []).map((t) => {
      const income = Number((uzs ? t.income_uzs : t.income) || 0);
      const salary = Number((uzs ? t.salary_expenses_uzs : t.salary_expenses) || 0);
      const operational = Number((uzs ? t.operational_expenses_uzs : t.operational_expenses) || 0);
      return {
        label: monthsShort[t.month - 1],
        income,
        salary,
        operational,
        // Derived rather than read from `profit` so the stack always closes on income.
        retained: Number((income - salary - operational).toFixed(2)),
      };
    });
    return trimTrailingEmpty(rows, ['income', 'salary', 'operational']);
  }, [monthlyTrend, displayCurrency, monthsShort]);

  const incomeRows = useMemo(
    () => trendRows.map((r) => ({ label: r.label, value: r.income })),
    [trendRows]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  // Employee without finance access: show only projects, salary, and team
  if (isEmployee && !canViewFinance) {
    return <EmployeeProjectsDashboard myProjects={myProjects} salaryData={mySalary} />;
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>

        {/* Month/Year/Currency selector */}
        <div className="flex flex-col items-start sm:items-end gap-1">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('filters.data')}</Label>
            <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
              <SelectTrigger className="w-[140px]" data-testid="currency-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">{t('filters.allCurrencies')}</SelectItem>
                <SelectItem value="USD">{t('filters.usdOnly')}</SelectItem>
                <SelectItem value="UZS">{t('filters.uzsOnly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('filters.display')}</Label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden h-10">
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'USD' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('USD')}
              >$ USD</button>
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'UZS' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('UZS')}
              >UZS</button>
            </div>
          </div>

          <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
            <SelectTrigger className="w-[140px]" data-testid="month-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month, idx) => (
                <SelectItem key={idx} value={(idx + 1).toString()}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
            <SelectTrigger className="w-[120px]" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <MonthRate month={selectedMonth} year={selectedYear} onRate={setExchangeRate} />
        </div>
      </div>

      {/* Key metrics. Salary and operational are shown as separate shares of
          income rather than rolled into one "Total Expenses" figure that also
          contains salary — that framing read as double-counting. */}
      {(() => {
        const uzs = displayCurrency === 'UZS';
        const income = Number((uzs ? summary?.total_income_uzs : summary?.total_income) || 0);
        const salary = Number((uzs ? summary?.salary_expenses_uzs : summary?.salary_expenses) || 0);
        const operational = Number((uzs ? summary?.operational_expenses_uzs : summary?.operational_expenses) || 0);
        const retained = Number((uzs ? summary?.net_profit_uzs : summary?.net_profit) || 0);
        const share = (v) => {
          const p = pctOf(v, income);
          return p === null ? '—' : t('stats.shareOfIncome', { percent: fmtPct(p) });
        };

        return (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatTile
                label={t('stats.incomeFor', { period: `${months[selectedMonth - 1]} ${selectedYear}` })}
                value={fmtMoney(income, displayCurrency)}
                icon={DollarSign}
                emphasis
              />
              <StatTile
                label={t('stats.salaries')}
                value={fmtMoney(salary, displayCurrency)}
                sub={share(salary)}
                icon={Users}
              />
              <StatTile
                label={t('stats.operational')}
                value={fmtMoney(operational, displayCurrency)}
                sub={share(operational)}
                icon={Receipt}
              />
              <StatTile
                label={t('stats.retained')}
                value={fmtMoney(retained, displayCurrency)}
                sub={share(retained)}
                tone={signTone(retained)}
                icon={TrendingUp}
              />
              <StatTile
                label={t('stats.activeProjects')}
                value={summary?.active_projects || 0}
                sub={`${months[selectedMonth - 1]} ${selectedYear}`}
                icon={FolderKanban}
              />
            </div>

            {income > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  {t('distribution.title')}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  {t('distribution.subtitle', { period: `${months[selectedMonth - 1]} ${selectedYear}`, total: fmtMoney(income, displayCurrency) })}
                </p>
                <DistributionBar
                  salary={salary}
                  operational={operational}
                  retained={retained}
                  currency={displayCurrency}
                />
              </div>
            )}
          </>
        );
      })()}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title={t('charts.incomeTrend', { year: selectedYear })}
          subtitle={t('charts.incomeTrendHint')}
          table={
            <SimpleTable
              columns={[
                { key: 'label', label: t('common:fields.month') },
                { key: 'income', label: t('charts.income'), align: 'right' },
              ]}
              rows={trendRows.map((r) => ({
                label: r.label,
                income: fmtMoney(r.income, displayCurrency),
              }))}
            />
          }
        >
          <IncomeTrendChart data={incomeRows} currency={displayCurrency} height={280} />
        </ChartCard>

        <ChartCard
          title={t('charts.composition')}
          subtitle={t('charts.compositionHint')}
          table={
            <SimpleTable
              columns={[
                { key: 'label', label: t('common:fields.month') },
                { key: 'salary', label: t('stats.salaries'), align: 'right' },
                { key: 'operational', label: t('charts.operational'), align: 'right' },
                { key: 'retained', label: t('stats.retained'), align: 'right' },
              ]}
              rows={trendRows.map((r) => ({
                label: r.label,
                salary: fmtMoney(r.salary, displayCurrency),
                operational: fmtMoney(r.operational, displayCurrency),
                retained: fmtMoney(r.retained, displayCurrency),
              }))}
            />
          }
        >
          <CompositionChart data={trendRows} currency={displayCurrency} height={280} />
          <VizLegend items={COMPOSITION_LEGEND} />
        </ChartCard>
      </div>

      {/* Employee with finance access: show their projects below charts */}
      {myProjects.length > 0 && (
        <div className="space-y-4 pt-2">
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight mb-1" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('employee.myProjects')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-4" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('employee.sectionSubtitle')}
            </p>
          </div>
          <EmployeeProjectsDashboard myProjects={myProjects} embedded />
        </div>
      )}
    </div>
  );
}

export default Dashboard;
